import { compose } from 'recompose'
import { connect } from 'react-redux'
import _ from 'lodash'
import { Helmet } from 'react-helmet'
import { Toast, Loader, Tip } from '@vzmi/shp_shared_compo'
import { FetchDataUtil, ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { withI13nWrapper } from '../Common/I13nWrapper'
import styles from './SalesPerformance.css'
import { sendI13nClickEvent } from '../../../lib/i13nUtil'
import safeAwait from '../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../lib/errorHelper'
import withConnectedExport from '../../containers/Common/WithConnectedExport'
import SPBaseModule from '../../lib/SPBaseModule'
import {
  getDefaultTimezoneMoment,
  numeralFormat,
  dollarFormat
} from '../../../lib/renderUtil'
import {
  DEFAULT_NO_FOUND_DESCRIPTION
} from '../../constants/globalTypes'
import { IconExportButton } from '../Common/ExportButton'
import Section from '../Common/ItemPage/Section/Section'
import TableAction from '../Common/NewDashboard/Table/TableAction/TableAction'
import Sort from '../Common/NewDashboard/Sort/Sort'
import {
  COMPONENT_KEY,
  RANKING_SIZE,
  transformToTableData,
  getListingsData,
  NewFeaturePromo
} from '../Dashboard/OperatingDashboard/CustomerInterest/CustomerInterest'
import {
  SORT_OPTIONS,
  SORT_MAP,
  QUANTITY_TYPE_FIELD_MAP,
  FILTER_NOTE
} from '../Dashboard/OperatingDashboard/CustomerInterest/constants'
import { SUB_ANCHOR_MAP } from '../Dashboard/constants'
import RankingTable, {
  FIELD as TABLE_FIELD,
  FIELD_MAP as TABLE_FIELD_MAP,
  getAllSpecInfo
} from '../Dashboard/OperatingDashboard/CustomerInterest/RankingTable/RankingTable'
import SummaryDate from '../Dashboard/OperatingDashboard/CustomerInterest/SummaryDate/SummaryDate'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './CustomerInterest.ga'
import withSharedModalsActions from '../Dashboard/OperatingDashboard/CustomerInterest/withSharedModalsActions'

const EXPORT_PARAMS = {
  offset: 0,
  limit: 50
}

export const PER_PAGE_SIZE = 20

export const EXPORT_MAX_SIZE = 1000

export const getParams = (state, withSort = true) => {
  const { currentPage, sortType, statsType } = state
  const offset = getCurrentOffset(currentPage)
  const currentSortType = `-${SORT_MAP[statsType][sortType]}`
  const params = {
    offset,
    limit: PER_PAGE_SIZE,
    ...(withSort && { sort: currentSortType })
  }
  return params
}

export const transformToSheetData = data => {
  return _.map(data, (item, index) => {
    const withSpec = !!item.spec
    const transItem = {
      ...item,
      ranking: index + 1,
      quantity: numeralFormat(item.quantity, { allowZero: true }),
      itemQuantity: numeralFormat(item.itemQuantity, { allowZero: true }),
      amount: dollarFormat(item.amount),
      itemPrice: dollarFormat(item.itemPrice),
      availableCount: numeralFormat(item.availableCount, { allowZero: true }),
      listingName: `${item.listingName}${
        withSpec ? '（' + getAllSpecInfo(item) + '）' : ''
      }`
    }
    // remove unused properties
    _.unset(transItem, 'spec')
    _.unset(transItem, 'parentSpec')
    return transItem
  })
}

const getCurrentOffset = page => PER_PAGE_SIZE * (page - 1)

const getStatsType = props => props.match.params.statsType || ''

class AbandonedCarts extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      currentPage: 1,
      sortType: SORT_OPTIONS[getStatsType(props)][0].value,
      statsType: getStatsType(props),
      resultsTotal: 0,
      data: [],
      rankingColumnProps: {
        rankingSize: RANKING_SIZE,
        width: 35,
        startIndex: 0
      },
      paginationProps: {}
    }
  }

  componentDidMount () {
    const {
      i13nMethods: { sendI13nPageViewByPageKey },
      match: { params: { statsType } }
    } = this.props
    this.updateAbandonedCartsTable(this.state)
    sendI13nPageViewByPageKey(`customerInterest_${statsType}`)
  }

  updateAbandonedCartsTable = state => this.loading(async () => {
    const { fetchListings } = this.props.actions
    const [errorMessage, payload] = await this.getAPIData(state)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    const {
      abandonedCarts,
      arrivalNoticeSummaries,
      listingTags,
      listings,
      pagination,
      reportDate
    } = payload
    const resultsTotal = _.get(pagination, 'resultsTotal')
    const paginationProps = {
      total: resultsTotal,
      onChange: this.onPageChange
    }
    // 取得賣場資訊
    let originalListings = {}
    if (state.statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link ||
      state.statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link) {
      const ids = state.statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link
        ? _.map(listings, item => item.id).join(',')
        : _.map(listingTags, item => item.listingId).join(',')
      this.setIsLoading(true)
      const isEmptyListingIds = _.isEmpty(ids)
      const req = isEmptyListingIds
        ? () => Promise.resolve([])
        : params => fetchListings(params).then(httpHandler)
      const [listingsError, listingsData = []] = await getListingsData(ids, req)
      originalListings = _.keyBy(listingsData, 'id')

      if (listingsError) {
        Toast.error(`取得賣場資料失敗：${listingsError.message}`)
        console.error(listingsError.error)
        this.setIsLoading(false)
        return
      }
    }
    const data = transformToTableData(
      state.statsType,
      abandonedCarts || arrivalNoticeSummaries || listingTags || [],
      listings || [],
      originalListings
    )
    this.setState({
      data,
      paginationProps,
      reportDate,
      resultsTotal
    })
    this.setIsSuccess(true)
  })

  getData = (statsType, actions) => {
    switch (statsType) {
      case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link:
        return actions.fetchAbandonedCarts
      case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ARRIVALNOTICE.link:
        return actions.fetchArrivalNoticeSummaries
      case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link:
        return actions.fetchListingTags
    }
  }

  getAPIData = async (state) => {
    const params = getParams(state)
    const [error, payload] = await safeAwait(
      this.getData(state.statsType, this.props.actions)(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    return [undefined, payload]
  }

  onTableSortChange = event => {
    const previousValue = this.state.sortType
    const newSortType = _.get(event, 'target.value', '')
    if (newSortType === previousValue) {
      return
    }

    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_SORT,
      payload: newSortType,
      statsType: this.state.statsType
    })
    const newState = {
      ...this.state,
      sortType: newSortType
    }
    this.setState(newState)
    this.updateAbandonedCartsTable(newState)
  }

  onPageChange = page => {
    const { sortType, rankingColumnProps } = this.state
    const newState = {
      ...this.state,
      currentPage: page,
      sortType,
      rankingColumnProps: {
        ...rankingColumnProps,
        startIndex: getCurrentOffset(page)
      }
    }
    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_PAGINATION,
      statsType: this.state.statsType
    })
    this.setState(newState)
    this.updateAbandonedCartsTable(newState)
  }

  getExcelSortColums = statsType => [
    TABLE_FIELD.LISTING_ID,
    TABLE_FIELD.LISTING_NAME,
    ...(QUANTITY_TYPE_FIELD_MAP[statsType]),
    ...(statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link ? [TABLE_FIELD.YESTERDAY_QUANTITY] : []),
    ...(
      statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link
        ? [TABLE_FIELD.ABANDONED_CART_QUANTITY, TABLE_FIELD.PAGE_VIEW_30D]
        : []
    ),
    TABLE_FIELD.ITEM_PRICE,
    TABLE_FIELD.AMOUNT,
    TABLE_FIELD.AVAILABLE_COUNT
  ]

  exportExcel = async () => {
    const {
      openExportModal,
      closeExportModal
    } = this.props
    const { statsType } = this.state
    const ExportHelper = new ExcelExportHelper({
      headers: {
        ranking: '排行',
        ...(this.getExcelSortColums(statsType).reduce((map, field) => {
          map[field] = TABLE_FIELD_MAP[field]
          return map
        }, {}))
      },
      fileName: `${statsType}_{{ nowDate.format("YYYYMMDDhhmmss") }}`,
      sheetName: '{{ reportDate }}',
      bookType: 'xlsx'
    })

    this.sendPageview({
      type: GA_CLICK_ACTION.EXPORT_EXCEL,
      statsType: this.state.statsType
    })
    openExportModal()

    const [error, payload] = await this.getTotalAbandonedCarts()
    if (error) {
      closeExportModal({ isError: true })
      return
    }

    const { reportDate } = payload
    const sheetData = transformToSheetData(
      _.get(payload, 'data', [])
    )
    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName({ reportDate }),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(sheetData)
        }
      ]
    }
    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }

  getTotalAbandonedCarts = async () => {
    const params = {
      ...getParams(this.state, false),
      ...EXPORT_PARAMS,
      maxResults: EXPORT_MAX_SIZE,
      currentPage: 1
    }
    let reportDate = ''
    const [error, payload] = await safeAwait(
      FetchDataUtil.fetchAllPages(params => {
        return this.getData(this.state.statsType, this.props.actions)(params)
          .then(res => {
            if (res.error) {
              throw res
            }
            return res
          })
      }, {
        payload: params,
        selector: res => {
          const {
            abandonedCarts,
            arrivalNoticeSummaries,
            listingTags,
            listings
          } = res
          reportDate = res.reportDate
          return transformToTableData(
            this.state.statsType,
            abandonedCarts || arrivalNoticeSummaries || listingTags || [],
            listings || []
          )
        }
      })
    )
    if (error) {
      return [error]
    }

    return [undefined, { ...payload, reportDate }]
  }

  sendPageview = (action) => {
    const { i13n } = this.props
    const i13nModel = getI13nModel(action)
    sendI13nClickEvent({ i13n, i13nModel })
  }

  sendTableLinkPageview = (payload) => {
    const statsType = this.state.statsType
    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_LINK,
      payload,
      statsType
    })
  }

  fetchListingsInfo = async (listingId, targetAudience) => {
    const { onOpenECouponModal, setECouponVisible } = this.props
    const { data } = this.state
    setECouponVisible(true)
    this.setIsLoading(true)
    await onOpenECouponModal(listingId, targetAudience, data)
    this.setIsLoading(false)
  }

  render () {
    const {
      text: title,
      supplier,
      flags,
      page
    } = this.props
    const {
      isLoading,
      isError,
      sortType,
      statsType,
      data,
      resultsTotal,
      reportDate,
      rankingColumnProps,
      paginationProps
    } = this.state
    const disableExportBtn = (
      isLoading ||
      (resultsTotal === 0)
    )
    const isPM = _.get(page, 'user.identity.isPM', false)

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Section
          title={(
            <div>
              {title}
              <IconExportButton
                disabled={disableExportBtn}
                data-role='export-button'
                className={styles.exportBtn}
                onClick={this.exportExcel}
                label='下載整筆檔案'
              />
              <Tip
                type='info'
                className={styles.tips}
              >
                最多下載前 1000 筆
              </Tip>
            </div>
          )}
        >
          <Loader loaded={!isLoading}>
            {
              isError
                ? this.renderIOError()
                : (
                  <>
                    <div className={styles.summaryWrapper}>
                      <SummaryDate
                        date={reportDate}
                        statsType={statsType}
                      />
                      <Tip
                        type='info'
                        className={styles.filterNoteTip}
                      >
                        {FILTER_NOTE}
                      </Tip>
                    </div>
                    <TableAction
                      className={styles.tableAction}
                      leftContent={(
                        <Tip
                          type='info'
                          className={styles.tips}
                        >
                          {`共 ${resultsTotal} 筆`}
                        </Tip>
                      )}
                    >
                      <NewFeaturePromo />
                      <Sort
                        id={COMPONENT_KEY}
                        options={SORT_OPTIONS[statsType]}
                        onChange={this.onTableSortChange}
                        sortType={sortType}
                      />
                    </TableAction>
                    <RankingTable
                      sharedLink={supplier.sharedLink}
                      data={data}
                      rankingColumnProps={rankingColumnProps}
                      noFoundDescription={DEFAULT_NO_FOUND_DESCRIPTION}
                      paginationProps={paginationProps}
                      sendTableLinkPageview={this.sendTableLinkPageview}
                      showPagination
                      statsType={statsType}
                      fetchListingsInfo={this.fetchListingsInfo}
                      flags={flags}
                      isPM={isPM}
                    />
                  </>
                )
            }
          </Loader>
        </Section>
      </>
    )
  }
}

const mapStateToProps = state => ({
  flags: state.flags,
  supplier: state.supplier,
  page: state.page
})

const connected = WrappedComponent => connect(
  mapStateToProps
)(WrappedComponent)

const hoc = compose(
  withI13nWrapper,
  withConnectedExport,
  withSharedModalsActions,
  connected
)

export default hoc(AbandonedCarts)
